import { Controller } from "@hotwired/stimulus"

// Drives a dropdown whose menu lives in the layout's :dropdowns yield rather
// than next to its toggle.
//
// Bootstrap's dropdown can't be used here: it looks for the menu as a sibling of
// the toggle. Keeping the menu out of the document flow is the point — inside a
// scrollable container (a table in .overflow__container) an absolutely
// positioned menu gets clipped and widens the scroll area.
//
// The menu is positioned with `position: fixed` against the toggle's rect, so no
// ancestor's overflow or transform can affect it.
export default class extends Controller {
    static targets = ["toggle"]
    static values = { menu: String, align: { type: String, default: "start" } }

    static GAP = 4

    connect() {
        this.onDocumentClick = this.onDocumentClick.bind(this)
        this.onKeydown = this.onKeydown.bind(this)
        this.reposition = this.reposition.bind(this)
    }

    disconnect() {
        this.close()
    }

    get menu() {
        return document.getElementById(this.menuValue)
    }

    get list() {
        return this.menu?.querySelector(".dropdown-menu")
    }

    get open() {
        return this.list?.classList.contains("show") ?? false
    }

    toggle(event) {
        event.preventDefault()
        this.open ? this.close() : this.show()
    }

    show() {
        const list = this.list
        if (!list) return

        // Only one portaled menu at a time.
        document.querySelectorAll(".dropdown--portal .dropdown-menu.show")
                .forEach((other) => other.classList.remove("show"))

        list.classList.add("show")
        this.toggleTarget.classList.add("show")
        this.toggleTarget.setAttribute("aria-expanded", "true")
        this.position()

        document.addEventListener("click", this.onDocumentClick, true)
        document.addEventListener("keydown", this.onKeydown)
        // Fixed positioning does not follow the page, so track both.
        window.addEventListener("scroll", this.reposition, true)
        window.addEventListener("resize", this.reposition)

        this.menu.querySelector("[data-dropdown-search-target='input']")?.focus()
    }

    close() {
        this.list?.classList.remove("show")
        this.toggleTarget?.classList.remove("show")
        this.toggleTarget?.setAttribute("aria-expanded", "false")

        document.removeEventListener("click", this.onDocumentClick, true)
        document.removeEventListener("keydown", this.onKeydown)
        window.removeEventListener("scroll", this.reposition, true)
        window.removeEventListener("resize", this.reposition)
    }

    position() {
        const list = this.list
        const anchor = this.toggleTarget.getBoundingClientRect()
        const menu = list.getBoundingClientRect()
        const gap = this.constructor.GAP

        // Below the toggle, unless the viewport bottom is closer than the menu is tall.
        const below = anchor.bottom + gap
        const flip = below + menu.height > window.innerHeight && anchor.top > menu.height
        const top = flip ? anchor.top - menu.height - gap : below

        // Aligned to the toggle, clamped so it never sticks out of the viewport —
        // which is what produced the horizontal scrollbar before.
        let left = this.alignValue === "end" ? anchor.right - menu.width : anchor.left
        left = Math.max(gap, Math.min(left, window.innerWidth - menu.width - gap))

        list.style.position = "fixed"
        list.style.top = `${Math.max(gap, top)}px`
        list.style.left = `${left}px`
        list.style.margin = "0"
    }

    reposition() {
        if (this.open) this.position()
    }

    onDocumentClick(event) {
        if (this.element.contains(event.target)) return
        if (this.menu?.contains(event.target)) {
            // A link inside the menu should still navigate.
            if (event.target.closest("a")) this.close()
            return
        }
        this.close()
    }

    onKeydown(event) {
        if (event.key !== "Escape") return
        // The search box clears itself first; only then does the menu close.
        if (event.target.closest("[data-dropdown-search-target='input']")?.value) return

        this.close()
        this.toggleTarget.focus()
    }
}
